import { isMobile } from 'react-device-detect';
import { combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { WorkspaceProductType } from '../models/Workspace';

export default class AppBarBloc {
  constructor({ authRepository, errorRepository, userRepository, casesRepository, freshchatService }) {
    this.authRepository = authRepository;
    this.errorRepository = errorRepository;
    this.userRepository = userRepository;
    this.casesRepository = casesRepository;
    this.freshchatService = freshchatService;
  }

  logout() {
    this.authRepository.logout();
    this.freshchatService.hideBubble();
  }

  get logoUrl$() {
    return this.authRepository.userWorkspace$.pipe(
      filter((e) => e),
      map((workspace) => workspace.logoUrl)
    );
  }

  get menu$() {
    return combineLatest([
      this.authRepository.auth$.pipe(filter((e) => e)),
      this.authRepository.userWorkspace$.pipe(filter((e) => e)),
      this.userRepository.userDetails$.pipe(filter((e) => e)),
      this.casesRepository.notPaidCases$.pipe(filter((e) => e)),
    ]).pipe(map((results) => this.getMenu(results[0].role, results[1], results[2], results[3])));
  }

  openFreshchatDialog() {
    this.freshchatService.openDialog();
  }

  getMenu(role, workspace, userDetails, notPaidCases) {
    const { availableReport } = userDetails;
    const notPaidCase = notPaidCases?.[0] || {};

    let patientDashboardLabel = 'My Video Reports';
    if (workspace.isMediphanyWorkspace) patientDashboardLabel = 'Personal Medical Record';
    if (workspace.productType === WorkspaceProductType.whiteGlove.value) patientDashboardLabel = 'My Personal Medical Record';

    const isCreatorMode = workspace?.creatorMode || false;
    const allowedRolesForCreatorMode = ['admin', 'creator', 'super-admin', 'trainee'];

    const menuMap = {
      admin: {
        button: {
          route: '/add-case',
          shouldAppear: () => workspace.caseCreationType !== 'ambra_gw',
          shouldListedAsButton: false,
          text: 'Request Video Report',
        },
        routes: [
          {
            name: 'Report Dashboard',
            path: '/cases',
          },
          {
            name: 'Creators',
            path: '/creators',
          },
          {
            name: 'Patients',
            path: '/patients',
          },
        ],
        userMenu: [
          {
            name: 'My Account',
            path: '/my-account',
          },
          {
            name: 'My Workspace',
            path: '/edit-workspace',
          },
        ],
      },
      creator: {
        button: {
          route: '/add-case',
          shouldAppear: () => workspace.caseCreationType !== 'ambra_gw',
          shouldListedAsButton: false,
          text: 'Request Video Report',
        },
        routes: [
          {
            name: 'Report Dashboard',
            path: '/cases',
          },
          {
            name: 'Patients',
            path: '/patients',
          },
        ],
        userMenu: [
          {
            name: 'My Account',
            path: '/my-account',
          },
        ],
      },
      'lead-tech': {
        logoPath: '/worklist',
        routes: [
          {
            name: 'Worklist',
            path: '/worklist',
          },
          {
            name: 'Resource Library',
            path: '/resources',
          },
          {
            name: 'Quizzes',
            path: '/quizzes',
          },
        ],
        userMenu: [
          {
            name: 'Quizzes',
            path: '/quizzes',
          },
          {
            name: 'Worklist',
            path: '/worklist',
          },
          {
            name: 'Resource Library',
            path: '/resources',
          },
          {
            name: 'My Account',
            path: '/my-account',
          },
        ],
      },
      patient: {
        button: {
          route: availableReport || (workspace.canRequestBeforePay && !notPaidCase?.id) ? '/personalized-video?fromNav=true' : '/package-plans',
          shouldAppear: () => workspace.isMediphanyWorkspace,
          shouldListedAsButton: false,
          text: availableReport ? 'Request Video Report' : 'Buy Video Report',
        },
        logoPath: '/patient-dashboard',
        routes: [
          {
            name: patientDashboardLabel,
            path: '/patient-dashboard',
          },
          {
            name: 'Self-Help Materials',
            path: '/self-help-materials',
          },
        ],
        userMenu: [
          {
            name: 'My Account',
            path: workspace.isMediphanyWorkspace ? '/patient-account' : '/my-account',
          },
          {
            name: workspace.isMediphanyWorkspace ? 'Personal Medical Record' : 'My Video Reports',
            path: '/patient-dashboard',
          },
          {
            name: 'Self-Help Materials',
            path: '/self-help-materials',
          },
          {
            name: 'Buy Video Report',
            path: '/personalized-video?fromNav=true',
            shouldAppear: () => workspace.isMediphanyWorkspace,
          },
        ],
      },
      radiologist: {
        logoPath: '/worklist',
        routes: [
          {
            name: 'Worklist',
            path: '/worklist',
          },
          {
            name: 'Resource Library',
            path: '/resources',
          },
          {
            name: 'Quizzes',
            path: '/quizzes',
          },
        ],
        userMenu: [
          {
            name: 'Quizzes',
            path: '/quizzes',
          },
          {
            name: 'Worklist',
            path: '/worklist',
          },
          {
            name: 'Resource Library',
            path: '/resources',
          },
          {
            name: 'My Account',
            path: '/my-account',
          },
        ],
      },
      'super-admin': {
        button: {
          route: '/new-workspace',
          shouldAppear: () => true,
          shouldListedAsButton: false,
          text: 'Create Workspace',
        },
        routes: [
          {
            name: 'Workspaces',
            path: '/workspaces',
          },
          {
            name: 'Report Dashboard',
            path: '/cases',
          },
          {
            name: 'Creators',
            path: '/creators',
          },
          {
            name: 'Patients',
            path: '/patients',
          },
          {
            name: 'Orders',
            path: '/orders',
          },
          {
            name: 'Resource Library',
            path: '/resources',
          },
          {
            name: 'Discounts',
            path: '/discounts',
          },
          {
            name: 'Logs',
            path: '/logs',
            shouldAppear: () => workspace.isMediphanyWorkspace,
          },
        ],
        userMenu: [
          {
            name: 'My Account',
            path: '/my-account',
          },
          {
            name: 'Workspaces',
            path: '/workspaces',
          },
          {
            name: 'Report Dashboard',
            path: '/cases',
          },
          {
            name: 'Creators',
            path: '/creators',
          },
          {
            name: 'Patients',
            path: '/patients',
          },
          {
            name: 'Orders',
            path: '/orders',
          },
          {
            name: 'Resource Library',
            path: '/resources',
          },
          {
            name: 'Discounts',
            path: '/discounts',
          },
          {
            name: 'Settings',
            path: '/edit-workspace',
          },
          {
            name: 'Logs',
            path: '/logs',
            shouldAppear: () => workspace.isMediphanyWorkspace,
          },
        ],
      },
      technologist: {
        logoPath: '/worklist',
        routes: [
          {
            name: 'Worklist',
            path: '/worklist',
          },
        ],
        userMenu: [
          {
            name: 'My Account',
            path: 'my-account',
          },
          {
            name: 'Worklist',
            path: '/worklist',
          },
        ],
      },
      trainee: {
        button: {
          route: '/add-case',
          shouldAppear: () => workspace.caseCreationType !== 'ambra_gw',
          shouldListedAsButton: false,
          text: 'Request Video Report',
        },
        routes: [
          {
            name: 'Report Dashboard',
            path: '/cases',
          },
          {
            name: 'Patients',
            path: '/patients',
          },
        ],
        userMenu: [
          {
            name: 'My Account',
            path: '/my-account',
          },
        ],
      },
    };

    const minimalMenu = {
      button: {
        route: '/add-case',
        shouldAppear: () => workspace.caseCreationType !== 'ambra_gw',
        shouldListedAsButton: true,
        text: 'Request Video Report',
      },
      routes: [
        {
          name: 'Dashboard',
          path: '/creator-dashboard',
        },
      ],
      userMenu: [
        {
          name: 'Dashboard',
          path: '/creator-dashboard',
        },
        {
          name: 'Request Video Report',
          path: '/creator-dashboard',
        },
        {
          name: 'My Account',
          path: '/my-account',
        },
        {
          name: 'Settings',
          path: '/edit-workspace',
        },
      ],
    };

    const adminTechQAMenu = {
      logoPath: '/worklist',
      routes: [
        {
          name: 'Worklist',
          path: '/worklist',
        },
        {
          name: 'Resource Library',
          path: '/resources',
        },
        {
          name: 'Quizzes',
          path: '/quizzes',
        },
        {
          name: 'Manage Users',
          path: '/users',
        },
      ],
      userMenu: [
        {
          name: 'Worklist',
          path: '/worklist',
        },
        {
          name: 'Quizzes',
          path: '/quizzes',
        },
        {
          name: 'Resource Library',
          path: '/resources',
        },
        {
          name: 'Manage Users',
          path: '/users',
        },
        {
          name: 'My Account',
          path: '/my-account',
        },
        {
          name: 'Settings',
          path: '/tech-qa-settings',
        },
      ],
    };

    if (workspace.productType === WorkspaceProductType.whiteGlove.value) {
      menuMap.creator = minimalMenu;
    } else if (workspace.productType === WorkspaceProductType.LawFirm.value) {
      menuMap.creator = minimalMenu;
      menuMap.admin = minimalMenu;
    } else if (workspace.productType === WorkspaceProductType.TechQA.value) {
      menuMap.admin = adminTechQAMenu;
    }

    allowedRolesForCreatorMode.forEach((currentRole) => {
      const currentMenu = menuMap[currentRole];
      const resourcesMenu = currentMenu.routes.find((route) => route.path === '/resources');
      if (!resourcesMenu?.path && isCreatorMode) {
        currentMenu.routes.push({
          name: 'Resource Library',
          path: '/resources',
        });
      }
    });

    if (!workspace?.isMediphanyWorkspace) {
      if (!workspace?.creatorMode) {
        menuMap.admin.routes = menuMap.admin.routes.filter((route) => route.path !== '/creators');
        menuMap['super-admin'].routes = menuMap['super-admin'].routes.filter((route) => route.path !== '/creators');
      }
    }

    return menuMap[role];
  }

  get userDetails$() {
    return this.userRepository.userDetails$;
  }
}
